// Mirrored from mju-ai-workbench/shared/schemas/agentRuns.ts — keep the two identical but for this line and the import extensions.
/**
 * The run model (Agent 365 C1d): what one turn of one agent session did, in
 * four kinds of record, carried to the platform in the exporter envelope's
 * `runs` array (./platform.ts) so the AIOE can build Agent 365's four span
 * kinds from them — `invoke_agent` from `run.start` + `run.end`, `chat` from
 * `run.chat`, `execute_tool` from `run.tool`, `output_messages` from the
 * `output` of `run.end`. The exact mapping is
 * docs/plans/2026-10-agent-365-aioe-contract.md.
 *
 * `runId` is the run ledger's id (src/acp/runLedger.ts) and becomes
 * `gen_ai.conversation.id`. `seq` is monotonic across restarts (it starts
 * from the clock), so the exporter's cursor survives one.
 *
 * Content fields (`input`, `output`, `arguments`, `result`) are present only
 * when the policy's `microsoft365.telemetryContent` allows them, after
 * masking and the DLP engine; `content` on every record says which rule was
 * applied, and `withheld` why a text was left out.
 */
import { z } from "zod";
import { telemetryContentSchema } from "./microsoft365.js";
const id = z.string().min(1).max(200);
const text = z.string().max(32_000);
/**
 * A run of a Foundry member (D273): where in Foundry it ran, for joining to Foundry's own traces
 * (Application Insights, Foundry tracing). `runId` stays the correlation id: one Foundry response is one
 * model round and one conversation spans many turns, so neither is our run. `responseIds` are the
 * turn's responses in order, at most 50 (the newest kept).
 */
export const runFoundrySchema = z.object({
    projectEndpoint: z.string().max(500),
    agentName: z.string().max(200).optional(),
    agentVersion: z.string().max(100).optional(),
    conversationId: id.optional(),
    responseIds: z.array(id).max(50).optional(),
    apiVersion: z.string().max(40).optional(),
});
export const runChannelSchema = z.enum(["workbench", "node", "routine"]);
export const runExecutionTypeSchema = z.enum(["HumanToAgent", "Agent2Agent"]);
/** What every record of a run repeats: enough to build one span without the others. */
const runCommon = {
    seq: z.number().int().nonnegative(),
    /** ISO time the record was made. */
    at: z.string(),
    runId: id,
    sessionId: id,
    /** The agent's name in its team. */
    agent: z.string().min(1).max(200),
    teamId: id,
    /** The specialist the member is (its `ref`), when it is one. */
    specialistId: id.optional(),
    /** The member's Entra agent identity, when the organisation has given it one (Phase A). */
    entraAgentId: id.optional(),
    /** The member's role in its team (ops-manager, implementer, reviewer …). */
    role: z.string().max(100).optional(),
    /** The person the run is for, by their id with the project's home (the AIOE maps it to an Entra object id). */
    principal: z.string().max(200).optional(),
    taskId: z.string().max(200).optional(),
    channel: runChannelSchema,
    /** Which content rule applied to this record. */
    content: telemetryContentSchema,
    /**
     * The member who ran it (D401, workbench-set, additive): `agent:<teamId>/<name>` for a member of a team, the principal id
     * of AIOE's roles (mju-aioe/docs/rbac.md section 5), or `specialist:<specialistId>` for a specialist on its own
     * (shared/schemas/workflows.ts `memberIdOf`); `memberKind` says which. Absent when neither is known, and on older records.
     */
    memberId: z.string().max(300).optional(),
    memberKind: z.enum(["specialist", "member"]).optional(),
};
export const runStartRecordSchema = z.object({
    ...runCommon,
    kind: z.literal("run.start"),
    startedAt: z.string(),
    executionType: runExecutionTypeSchema,
    /** Agent2Agent: the agent that handed the work over. */
    caller: z.object({ agent: z.string().max(200), entraAgentId: id.optional(), sessionId: id.optional() }).optional(),
    /** person / agent / automation (shared/schemas/projectRuns.ts). */
    trigger: z.string().max(40),
    routine: z.object({ id, name: z.string().max(200).optional() }).optional(),
    runtime: z.string().max(100).optional(),
    model: z.string().max(200).optional(),
    planId: id.optional(),
    conversationId: id.optional(),
    /** The prompt, under the content rule. */
    input: text.optional(),
    withheld: z.string().max(200).optional(),
    /** A Foundry member's run (D273): the project, agent and conversation, for joining to Foundry's traces. */
    foundry: runFoundrySchema.optional(),
    /**
     * The context-budget profile the agent ran on (D272, workbench-set, additive): `lean`, `standard` or `full`
     * (the workbench's shared/teams/contextProfiles.ts), so overflow and cost views can group runs by it.
     */
    contextProfile: z.enum(["lean", "standard", "full"]).optional(),
});
export const runToolRecordSchema = z.object({
    ...runCommon,
    kind: z.literal("run.tool"),
    toolCallId: id,
    toolName: z.string().min(1).max(300),
    /** `mcp` for a tool server's tool (through the tool gate or named `mcp__…`), `builtin` for the runtime's own. */
    toolType: z.enum(["mcp", "builtin"]),
    /** The tool server, for an MCP tool the gate saw. */
    server: z.string().max(200).optional(),
    /** ACP's kind: read, edit, execute, fetch … */
    toolKind: z.string().max(40).optional(),
    startedAt: z.string(),
    endedAt: z.string().optional(),
    status: z.enum(["completed", "failed", "cancelled", "pending"]),
    /** SHA-256 over the arguments' canonical JSON (src/acp/toolGovernance.ts `argumentsDigest`). */
    argumentsDigest: z.string().max(100).optional(),
    /** The arguments, masked (`maskArguments`), under the content rule, as JSON text. */
    arguments: text.optional(),
    /** What the tool gate decided, when the call went through it. */
    gate: z
        .object({
        decision: z.string().max(40),
        by: z.string().max(100).optional(),
        risk: z.string().max(40).optional(),
        enforced: z.boolean().optional(),
    })
        .optional(),
    /** Who answered the permission request, when one was asked. */
    approval: z
        .object({
        decision: z.string().max(40),
        actor: z.object({ kind: z.string().max(20), id: z.string().max(200).optional(), principal: z.string().max(200).optional() }),
    })
        .optional(),
    withheld: z.string().max(200).optional(),
});
export const runChatRecordSchema = z.object({
    ...runCommon,
    kind: z.literal("run.chat"),
    startedAt: z.string(),
    durationMs: z.number().nonnegative(),
    /** `gen_ai.provider.name` (the proxy's `gen_ai.system`). */
    provider: z.string().max(100),
    model: z.string().max(200),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    status: z.enum(["ok", "error", "cancelled"]),
    stream: z.boolean().optional(),
    toolCalls: z.number().int().nonnegative().optional(),
    costUsd: z.number().nonnegative().optional(),
    dlpAction: z.string().max(20).optional(),
    dlpReason: z.string().max(300).optional(),
    blocked: z.boolean().optional(),
    error: z.string().max(300).optional(),
    /** A Foundry member's model round (D273): the Foundry response it was. */
    foundry: z.object({ responseId: id }).optional(),
});
/** Why a turn left its model for the next in its class's chain (D312). */
export const runFallbackReasonSchema = z.enum(["rate-limited", "overloaded", "dropped", "refused"]);
/** The model of the class's fallback chain that finished a turn (D312). */
export const runFallbackSchema = z.object({
    provider: z.string().min(1).max(100),
    model: z.string().min(1).max(200),
    reason: runFallbackReasonSchema,
});
export const runEndRecordSchema = z.object({
    ...runCommon,
    kind: z.literal("run.end"),
    startedAt: z.string(),
    endedAt: z.string(),
    outcome: z.enum(["ok", "error", "cancelled"]),
    tokens: z.object({ input: z.number().int().nonnegative(), output: z.number().int().nonnegative() }).optional(),
    toolCalls: z.number().int().nonnegative().optional(),
    costUsd: z.number().nonnegative().optional(),
    /** The final message, under the content rule. */
    output: text.optional(),
    withheld: z.string().max(200).optional(),
    /** A Foundry member's run (D273): as on `run.start`, with every response of the turn. */
    foundry: runFoundrySchema.optional(),
    /**
     * How many times the context was tidied up during the run (D272 part 3, workbench-set, additive): the proxy
     * shortening tool descriptions or older tool results to fit, the context guard compacting or resetting.
     */
    compactions: z.number().int().positive().optional(),
    /** The context overflowed at least once during the run (D272 part 3). */
    overflowed: z.literal(true).optional(),
    /**
     * The turn finished on a model of its class's fallback chain rather than the one it started on (D312, the policy's
     * `providers.fallback`; workbench-set, additive): the provider and model that finished it, and why it moved.
     */
    fallbackTo: runFallbackSchema.optional(),
    /** How long the run took, end minus start (D401, workbench-set, additive): what run time and success tiles add up. */
    durationMs: z.number().int().nonnegative().optional(),
    /**
     * The member handed the work back rather than finishing it (D401, additive): a task given back to the person or the team,
     * whatever `outcome` the turn itself ended on. {@link agentRunOutcome} reads the two together.
     */
    handedBack: z.literal(true).optional(),
});
/**
 * How a run ended, in the four words the agent and team Overview counts (D401; AIOE's run row stores it): `succeeded`,
 * `failed`, `cancelled`, or `handed_back`. Read from `run.end` by {@link agentRunOutcome}: `outcome` stays the turn's own
 * (`ok`, `error`, `cancelled`, which Agent 365's spans need), so records from older builds keep their meaning.
 */
export const AGENT_RUN_OUTCOMES = ["succeeded", "failed", "cancelled", "handed_back"];
export function agentRunOutcome(end) {
    if (end.handedBack)
        return "handed_back";
    return end.outcome === "ok" ? "succeeded" : end.outcome === "error" ? "failed" : "cancelled";
}
export const runRecordSchema = z.discriminatedUnion("kind", [runStartRecordSchema, runToolRecordSchema, runChatRecordSchema, runEndRecordSchema]);
//# sourceMappingURL=agentRuns.js.map